import { BCMSDate, BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, StateManager, useBCMSContext, useModel, useStateManager } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from 'components/custom-ui/CustomSelect';
import DateSelect from 'components/custom-ui/DateSelect';
import { FormContainer, FormRowView, TableBody, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableItemView, TableView, WidgetContainer, WidgetView } from 'components/shared';
import { FormButtonRow } from 'components/shared/containers/FormContainer';
import { PrimaryButton, UICheckbox, UITextInput } from 'components/ui';
import Classes, { Account, OutgoingFedWire } from "model";
import React from 'react';
import { SVGAsset } from 'res';
import SearchForm from "./classes/SearchForm";
import "./styles.css";
import "./mobile.css";
import DateRangeCustomSelect, { getDateRangeObj } from "components/custom-ui/DateRangeCustomSelect";

/**
 * @function OutgoingFedWiresWidget
 * @return {React.Component}
 * @exported true
 * @description - The Widget that shows fed wires.
 */

export default function OutgoingFedWiresWidget(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);
  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      onChange: prop => value => {
        const crud = BCMSStateManagerCRUD.init(state);

        if(prop === "showSearchOptions") {
          const updatedVal = { isActive: value };
          crud.update([ "ui", prop ], updatedVal);
          
          const modifyPageUIFromWidget = props.action.modifyPageUIFromWidget("outgoingFedWiresWidgetUI", "showSearchOptions");
          modifyPageUIFromWidget(updatedVal);
        } else {
          crud.update([ "data", prop ], value)
        }

        crud.submit(setState);
      },

      onSearchChange: prop => value => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "search", prop ], value)
        .submit(setState);
      },

      onSearchClick: () => {
        const filtered = state.data.search.filter();
        
        BCMSStateManagerCRUD.init(state)
        .update([ "ui", "showSearchOptions", "isActive" ], false)
        .update([ "data", "outgoingFedWires" ], filtered)
        .submit(setState);
      },

      onResetClick: () => {
        const resetSearch = state.data.search.reset();
        const filtered = resetSearch.filter();
        
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "search" ], resetSearch)
        .update([ "ui", "showSearchOptions", "isActive" ], false)
        .update([ "data", "outgoingFedWires" ], filtered)
        .submit(setState);
      },

      // Updates the date ranges in the "From" and "To" fields.
      onChangeDateRange: dataObj => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "search", "fromDate" ], dataObj.fromDate)
        .update([ "data", "search", "toDate" ], dataObj.toDate)
        .update([ "data", "search", "dateRange" ], dataObj.value)
        .submit(setState);
      }
  });

  return(
    <StateManager state={ { data: data, ui: ui } } controller={ controller }>
      <WidgetContent/>
    </StateManager>
  )
}

export const getDefaultData = () => ({
  accounts: Account.initAllAsset(),
  outgoingFedWires: OutgoingFedWire.initAll(),
  search: SearchForm.init({ id: "0", status: "any status", fundingAccountID: "all accounts" })
});

export const getDefaultUI = () => ({
  // no ui changes for now
  showSearchOptions: { isActive: false }
});

function WidgetContent(props) {
  const { data, ui, onChange, onSearchChange, onChangeDateRange, onSearchClick, onResetClick } = useStateManager();
  const hasWires = data.outgoingFedWires.length > 0;
  const search = data.search ?? {};
  const wireRows = data.outgoingFedWires.filter(item => item.id !== undefined).reduce((acc, wire) => {
    acc.push(<WireTableRow key={ wire.id } wire={ wire } />);
    return acc;
  }, []);

  const mobileWireRows = data.outgoingFedWires.filter(item => item.id !== undefined).reduce((acc, wire) => {
    acc.push(<MobileWireRow key={ wire.id } wire={ wire } />);
    return acc;
  }, []);

  const accounts = data.accounts.reduce((acc, account, index) => {
    let label = ``;
    if(account.type === "loan") {
      label = `${ account.number } - ${ account.nickname } (Current ${ account.currentBalance.format() })`;
    } else if(account.type === "loc") {
      label = `${ account.number } - ${ account.nickname } (Available ${ account.availableCredit.format() })`;
    } else {
      label = `${ account.number } - ${ account.nickname } (Available ${ account.availableBalance.format() })`;
    }

    if(index === 0) { acc.push(<CustomSelectItem key="allAccounts" value="all accounts">All Accounts</CustomSelectItem>) }
    acc.push(<CustomSelectItem key={ account.id } value={ account.id }>{ label }</CustomSelectItem>);
    return acc;
  }, []);

  return(
    <WidgetContainer className="outgoing-fed-wires-widget">
      <WidgetView text="Outgoing Fed Wires Summary" dataPoint="OutgoingFedWiresSummary">
        <UICheckbox label="Show Search Options" value={ ui.showSearchOptions.isActive ? "checked" : "" } onChange={ onChange("showSearchOptions") } />
        { ui.showSearchOptions.isActive && 
        <FormContainer className="small-form-width">
          <FormRowView text="Status">
            <CustomSelect value={ search.status } 
            onChange={ onSearchChange("status") }>
                <CustomSelectItem value="any status">Any Status</CustomSelectItem>
                <CustomSelectItem value="completed">Completed</CustomSelectItem>
                <CustomSelectItem value="rejected by federal reserve">Rejected By Federal Reserve</CustomSelectItem>
                <CustomSelectItem value="pending">Pending</CustomSelectItem>
                <CustomSelectItem value="rejected">Rejected</CustomSelectItem>
            </CustomSelect>
          </FormRowView>
          <FormRowView text="Funding Account">
            <CustomSelect value={ search.fundingAccountID } onChange={ onSearchChange("fundingAccountID") }>
              { accounts }
            </CustomSelect>
          </FormRowView>
          <FormRowView text="Date Range">
            <DateRangeCustomSelect value={ search.dateRange } onChange={ onChangeDateRange } />
          </FormRowView>
          <FormRowView text="From:">
            <DateSelect value={ search.fromDate } enablePast={ true } enableFuture={ false } onChange={ onSearchChange("fromDate") } />
          </FormRowView>
          <FormRowView text="To:">
            <DateSelect value={ search.toDate } onChange={ onSearchChange("toDate") } />
          </FormRowView>
          <FormRowView text="Reference Number">
            <UITextInput value={ search.referenceNumber } onChange={ onSearchChange("referenceNumber") } />
          </FormRowView>
          <FormButtonRow>
            <DisabledPrompt>
              <PrimaryButton text="Search" onClick={ onSearchClick } />
            </DisabledPrompt>
          </FormButtonRow>
        </FormContainer>
        }
        <div className="show-on-desktop">
          <TableView>
            <TableColumnContainer>
              <TableColumnView className="text-left ref-number-text" text="Ref #" />
              <TableColumnView className="text-left payee-text" text="Payee" />
              <TableColumnView className="text-left funding-account-text" text="Funding Account" />
              <TableColumnView className="text-left amount-text" text="Amount" />
              <TableColumnView className="text-left status-text" text="Status" />
              <TableColumnView className="text-left wire-date-text" text=
                {<>Wire Date
                  <DisabledPrompt>
                    <SVGAsset id="ArrowDownIcon" className="clickable" />
                  </DisabledPrompt>
                </>} 
              />
              
              <TableColumnView className="text-left fed-details-text" text="Fed Details" />
              <TableColumnView className="text-left search-column" text="" />
            </TableColumnContainer>
            <TableBody>
              { hasWires && wireRows }
              { !hasWires && <NoWireTransferTableRow colspan="8" /> }
            </TableBody>
          </TableView>
        </div>
        <div className="mobile-outgoing-fed-wires-widget show-on-mobile">
          { hasWires && mobileWireRows }
          <TableView>
            <TableBody>
              { !hasWires && <NoWireTransferTableRow colspan="8" /> }
            </TableBody>
          </TableView>
        </div>
        <div className="button-container flex-container flex-horizontal">
          <PrimaryButton className="disabled-button" text="Export to Excel (CSV)" />
          <PrimaryButton className="disabled-button" text="Export to PDF" />
        </div>
      </WidgetView>
    </WidgetContainer>
  );
}

/**
 * @function WireTableRow
 * @returns {React.Component}
 * @exported false
 * @description - A table row.
 */
function WireTableRow(props) {
  const wire = props.wire ?? {};
  let status = wire.status;

  return (
    <TableItemContainer>
      <TableItemTextView text={ wire.referenceNumber } />
      <TableItemTextView text={ wire.payee.nickname } />
      <TableItemTextView text={ `${ wire.fundingAccount.number } - ${ wire.fundingAccount.nickname }` } />
      <TableItemTextView text={ wire.amount.format() } />
      <TableItemTextView text={ statusMap[status] } />
      <TableItemTextView text={ wire.wireDate.format("m/d/Y") } />
      <TableItemTextView text={ 
        <div className="fed-details-description-container">
          <p className="fed-detail-imad-description-container">
            <b>IMAD: </b>
            <span>{ `${ wire.imad }` }</span>
          </p>
          <p className="fed-detail-omad-description-container">
            <b>OMAD: </b>
            <span>{ `${ wire.omad }` }</span>
          </p>
        </div>
       } />
      <TableItemView className="wire-history-table-row-icons text-right">
        <DisabledPrompt>
          <SVGAsset id="MagnifyingGlassIcon" className="clickable" />
        </DisabledPrompt>
      </TableItemView>
    </TableItemContainer>
  );
}

/**
 * @function MobileWireRow
 * @return {React.Component}
 * @exported false
 * @description The row that shows in the MobileWireRow
 */

function MobileWireRow(props) {
  const wire = props.wire ?? {};
  let status = wire.status;

  return(
    <div className="mobile-outgoing-fed-wires-row flex-container flex-horizontal align-center space-between">
      <div className="wire-details-column flex-width">
        <div className="flex-container flex-horizontal space-between wires-item">
          <b className="flex-item set-width">{ wire.referenceNumber }</b>
          <p className="set-item flex-width text-right">{ wire.amount.format({ style:"decimal" }) }</p>
        </div>
        <div className="flex-container flex-horizontal space-between wires-item">
          <p className="flex-item set-width">Currency:</p>
          <p className="set-item flex-width text-right"></p>
        </div>
        <div className="flex-container flex-horizontal space-between wires-item">
          <p className="flex-item set-width">Amount in $:</p>
          <p className="set-item flex-width text-right">{ wire.amount.format() }</p>
        </div>
        <div className="flex-container flex-horizontal space-between wires-item">
          <p className="flex-item set-width">Wire Date:</p>
          <p className="set-item flex-width text-right">{ wire.wireDate.format('m/d/Y') }</p>
        </div>
        <div className="flex-container flex-horizontal space-between wires-item status">
          <p className="flex-item set-width">Status:</p>
          <p className="set-item flex-width text-right">{ statusMap[status] }</p>
        </div>
        <div className="flex-container flex-horizontal space-between wires-item status">
          <p className="flex-item set-width">Fed Details:</p>
          <p className="set-item flex-width text-right">{ 
            <div className="mobile-fed-details-description-container">
              <p className="mobile-fed-detail-imad-description-container">
                <b>IMAD: </b>
                <span>{ `${ wire.imad }` }</span>
              </p>
              <p className="mobile-fed-detail-omad-description-container">
                <b>OMAD: </b>
                <span>{ `${ wire.omad }` }</span>
              </p>
            </div>
           }</p>
        </div>
      </div>
      <DisabledPrompt>
        <SVGAsset id="MagnifyingGlassIcon" className="inline-text-icon left-icon clickable set-width" />
      </DisabledPrompt>
    </div>
  );
}

/**
 * @function NoWireTransferTableRow
 * @param {String} colspan - allows us to set how many rows the table data will span
 * @return {React.Component}
 * @exported true
 * @description The table row that shows no wire transfers
 */

function NoWireTransferTableRow(props){
  const colspan = props.colspan ?? "1";

  return(
    <TableItemContainer className="no-wire-transfer-table-row">
      <td colSpan={colspan} className="text-center table-item-text-view">There are no wire transfers to display</td>
    </TableItemContainer>
  );
}

const statusMap = {
  "completed": "Completed",
  "rejected by federal reserve": "Rejected by Federal Reserve",
  "pending": "Pending",
  "rejected": "Rejected"
};